<ion-header [translucent]="true">
  <ion-toolbar>
    <ion-buttons slot="start">
      <ion-button (click)="goBack()">
        <ion-icon name="arrow-back" slot="icon-only"></ion-icon>
      </ion-button>
    </ion-buttons>
    <ion-title>Taxonomy Classification Dashboard</ion-title>
    <ion-buttons slot="end">
      <ion-button (click)="onRefresh()" [disabled]="isLoading">
        <ion-icon name="refresh-outline" slot="icon-only"></ion-icon>
      </ion-button>
    </ion-buttons>
  </ion-toolbar>
</ion-header>

<ion-content [fullscreen]="true">
  <ion-grid style="margin: 3px">
    <!-- Error State -->
    <ion-row *ngIf="error">
      <ion-col>
        <ion-card color="danger">
          <ion-card-header>
            <ion-card-title>Error</ion-card-title>
          </ion-card-header>
          <ion-card-content>
            {{ error }}
            <br />
            <ion-button
              (click)="onRefresh()"
              color="light"
              size="small"
              style="margin-top: 1rem"
            >
              <ion-icon name="refresh-outline" slot="start"></ion-icon>
              Try Again
            </ion-button>
          </ion-card-content>
        </ion-card>
      </ion-col>
    </ion-row>

    <!-- Dashboard Header -->
    <ion-row
      *ngIf="tenant && currentDimension"
      class="ion-justify-content-between ion-align-items-center"
      style="margin-bottom: 2rem"
    >
      <ion-col>
        <h1>{{ currentDimension.dimensionName }} Classification Dashboard</h1>
        <p style="margin: 0.25rem 0 0 0; font-size: 0.9rem">
          Monitor the progress of automatic article categorization for
          {{ tenant.name }}
        </p>
      </ion-col>
      <ion-col size="auto">
        <div style="display: flex; gap: 0.5rem">
          <ion-button
            color="primary"
            (click)="startOnboarding()"
            [disabled]="isLoading"
          >
            <ion-icon name="play-outline" slot="start"></ion-icon>
            Start Onboarding
          </ion-button>
          <ion-button
            color="danger"
            fill="outline"
            (click)="resetTaxonomy()"
            [disabled]="isLoading"
          >
            <ion-icon name="trash-outline" slot="start"></ion-icon>
            Reset Taxonomy
          </ion-button>
          <ion-button
            color="secondary"
            fill="outline"
            (click)="viewDimensionalAnalysis()"
            [disabled]="isLoading"
          >
            <ion-icon name="analytics-outline" slot="start"></ion-icon>
            Dimensional Analysis
          </ion-button>
        </div>
      </ion-col>
    </ion-row>

    <!-- Loading State -->
    <ion-row
      *ngIf="isLoading"
      class="ion-justify-content-center"
      style="padding: 3rem"
    >
      <ion-col size="auto" class="ion-text-center">
        <ion-spinner color="primary"></ion-spinner>
        <p style="margin-top: 1rem">Loading taxonomy statistics...</p>
      </ion-col>
    </ion-row>

    <!-- Dashboard Content -->
    <div *ngIf="!isLoading && !error && currentDimension && stats">
      <!-- Classification Overview -->
      <ion-row>
        <ion-col size="12" size-md="6">
          <ion-card>
            <ion-card-header>
              <ion-card-title
                >{{ currentDimension.dimensionName }} Classification
                Overview</ion-card-title
              >
              <ion-card-subtitle
                >Dimension Classification Progress</ion-card-subtitle
              >
            </ion-card-header>
            <ion-card-content>
              <div style="margin-bottom: 1.5rem">
                <div
                  style="
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 0.5rem;
                  "
                >
                  <span style="font-weight: 500">Classification Progress</span>
                  <span style="font-weight: 600"
                    >{{ currentDimension.progressPercent }}%</span
                  >
                </div>
                <ion-progress-bar
                  [value]="currentDimension.progressPercent / 100"
                  [color]="getProgressColor(currentDimension.progressPercent)"
                ></ion-progress-bar>
                <div
                  style="
                    font-size: 0.9rem;
                    color: var(--ion-color-medium);
                    margin-top: 0.5rem;
                  "
                >
                  {{ currentDimension.classifiedArticles }} of
                  {{ currentDimension.totalArticles }} articles classified
                </div>
              </div>

              <div style="text-align: center; margin-top: 2rem">
                <div
                  style="
                    font-size: 3rem;
                    font-weight: 700;
                    line-height: 1;
                    margin-bottom: 1rem;
                  "
                >
                  {{ currentDimension.classifiedArticles }}
                </div>
                <div style="font-size: 1.25rem; color: var(--ion-color-medium)">
                  Articles Classified
                </div>
                <div style="margin-top: 1rem">
                  <ion-chip
                    [color]="getCompletionColor(currentDimension.isComplete)"
                  >
                    {{ getCompletionText(currentDimension.isComplete) }}
                  </ion-chip>
                </div>
              </div>
            </ion-card-content>
          </ion-card>
        </ion-col>

        <ion-col size="12" size-md="6">
          <ion-card>
            <ion-card-header>
              <ion-card-title
                >{{ currentDimension.dimensionName }} Classification
                Configuration</ion-card-title
              >
              <ion-card-subtitle
                >Settings for selected dimension</ion-card-subtitle
              >
            </ion-card-header>
            <ion-card-content>
              <ion-item lines="none">
                <ion-label>
                  <h3>Strategy</h3>
                  <p>{{ currentDimension.strategy }}</p>
                </ion-label>
                <ion-chip
                  [color]="getStrategyColor(currentDimension.strategy)"
                  slot="end"
                >
                  {{ currentDimension.strategy }}
                </ion-chip>
              </ion-item>

              <div *ngIf="stats.strategySettings" style="margin-top: 1rem">
                <div
                  style="
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 0.5rem;
                  "
                >
                  <h4 style="margin: 0; font-size: 0.9rem; font-weight: 600">
                    Confidence Thresholds
                  </h4>
                  <ion-button
                    *ngIf="!isEditingThresholds"
                    size="small"
                    fill="clear"
                    (click)="startEditingThresholds()"
                    [disabled]="isLoading"
                  >
                    <ion-icon name="create-outline" slot="start"></ion-icon>
                    Edit
                  </ion-button>
                </div>

                <!-- Read-only view -->
                <div
                  *ngIf="!isEditingThresholds"
                  style="
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 1rem;
                    margin-top: 0.5rem;
                  "
                >
                  <div style="text-align: center">
                    <div
                      style="font-weight: 600; color: var(--ion-color-primary)"
                    >
                      LLM: {{ stats.strategySettings.confidenceThresholds.llm }}
                    </div>
                  </div>
                  <div style="text-align: center">
                    <div
                      style="
                        font-weight: 600;
                        color: var(--ion-color-secondary);
                      "
                    >
                      Centroid:
                      {{ stats.strategySettings.confidenceThresholds.centroid }}
                    </div>
                  </div>
                </div>

                <!-- Edit view -->
                <div *ngIf="isEditingThresholds">
                  <div
                    style="
                      display: grid;
                      grid-template-columns: 1fr 1fr;
                      gap: 1rem;
                      margin: 1rem 0;
                    "
                  >
                    <div>
                      <ion-label
                        style="
                          font-size: 0.8rem;
                          color: var(--ion-color-primary);
                        "
                        >LLM</ion-label
                      >
                      <ion-input
                        type="number"
                        step="0.01"
                        min="0"
                        max="1"
                        [value]="editThresholds.llm"
                        (ionInput)="editThresholds.llm = +$event.target.value"
                        style="
                          --background: var(--ion-color-light);
                          margin-top: 0.25rem;
                        "
                      ></ion-input>
                    </div>
                    <div>
                      <ion-label
                        style="
                          font-size: 0.8rem;
                          color: var(--ion-color-secondary);
                        "
                        >Centroid</ion-label
                      >
                      <ion-input
                        type="number"
                        step="0.01"
                        min="0"
                        max="1"
                        [value]="editThresholds.centroid"
                        (ionInput)="
                          editThresholds.centroid = +$event.target.value
                        "
                        style="
                          --background: var(--ion-color-light);
                          margin-top: 0.25rem;
                        "
                      ></ion-input>
                    </div>
                  </div>

                  <div
                    style="
                      display: flex;
                      gap: 0.5rem;
                      justify-content: flex-end;
                      margin-top: 1rem;
                    "
                  >
                    <ion-button
                      size="small"
                      fill="clear"
                      color="medium"
                      (click)="cancelEditingThresholds()"
                      [disabled]="isSavingThresholds"
                    >
                      Cancel
                    </ion-button>
                    <ion-button
                      size="small"
                      (click)="saveThresholds()"
                      [disabled]="isSavingThresholds"
                    >
                      <ion-spinner
                        name="crescent"
                        *ngIf="isSavingThresholds"
                      ></ion-spinner>
                      <span *ngIf="!isSavingThresholds">Save</span>
                    </ion-button>
                  </div>
                </div>
              </div>
            </ion-card-content>
          </ion-card>
        </ion-col>
      </ion-row>

      <!-- Categories Loading -->
      <ion-row *ngIf="isLoadingCategories">
        <ion-col>
          <ion-card>
            <ion-card-header>
              <ion-card-title>Categories</ion-card-title>
            </ion-card-header>
            <ion-card-content class="ion-text-center">
              <ion-spinner color="primary"></ion-spinner>
              <p style="margin-top: 1rem">Loading categories...</p>
            </ion-card-content>
          </ion-card>
        </ion-col>
      </ion-row>

      <!-- Categories -->
      <ion-row
        *ngIf="
          !isLoadingCategories && categories && categories.categories.length > 0
        "
      >
        <ion-col>
          <ion-card>
            <ion-card-header>
              <ion-card-title
                >Categories ({{ categories.categories.length }})</ion-card-title
              >
              <ion-card-subtitle
                >Discovered categories for
                {{ currentDimension?.dimensionName }}</ion-card-subtitle
              >
            </ion-card-header>
            <ion-card-content>
              <div
                *ngFor="
                  let category of categories.categories;
                  trackBy: trackByCategoryDetailed
                "
              >
                <!-- Category Header Card -->
                <ion-card>
                  <ion-card-header>
                    <div
                      style="
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                      "
                    >
                      <div
                        style="display: flex; align-items: center; gap: 0.5rem"
                      >
                        <ion-card-title>{{ category.category }}</ion-card-title>
                        <ion-chip color="primary"
                          >{{ category.articleCount }} articles</ion-chip
                        >
                      </div>
                      <ion-button
                        fill="clear"
                        size="small"
                        (click)="viewCategoryAnalysis(category.category)"
                      >
                        <ion-icon
                          name="analytics-outline"
                          slot="icon-only"
                        ></ion-icon>
                      </ion-button>
                    </div>
                  </ion-card-header>

                  <ion-card-content>
                    <!-- Articles List -->
                    <div *ngIf="category.articles.length > 0">
                      <ion-item
                        *ngFor="
                          let article of category.articles;
                          trackBy: trackByArticle
                        "
                        lines="none"
                      >
                        <ion-label>
                          <a
                            [href]="getTaxonomyArticleUrl(article.id)"
                            (click)="onTaxonomyArticleClick($event, article.id)"
                            style="
                              text-decoration: none;
                              color: var(--ion-color-primary);
                              font-weight: 500;
                            "
                          >
                            {{ article.title }}
                          </a>
                          <p
                            *ngIf="article.projectId"
                            style="
                              margin: 0.25rem 0 0 0;
                              font-size: 0.8rem;
                              color: var(--ion-color-medium);
                            "
                          >
                            Project: {{ article.projectId }}
                          </p>
                        </ion-label>
                        <ion-chip slot="end" color="medium" size="small">
                          {{ article.projectId ? "Project" : "Global" }}
                        </ion-chip>
                        <a
                          [href]="getTenantArticleUrl(article.id)"
                          target="_blank"
                          rel="noopener noreferrer"
                          slot="end"
                        >
                          <ion-button
                            fill="clear"
                            size="small"
                            color="secondary"
                          >
                            <ion-icon
                              name="open-outline"
                              slot="icon-only"
                            ></ion-icon>
                          </ion-button>
                        </a>
                      </ion-item>
                    </div>

                    <div
                      *ngIf="category.articles.length === 0"
                      class="ion-text-center"
                      style="padding: 1rem; color: var(--ion-color-medium)"
                    >
                      <ion-icon
                        name="document-outline"
                        size="large"
                        style="opacity: 0.5"
                      ></ion-icon>
                      <p>No articles found in this category</p>
                    </div>
                  </ion-card-content>
                </ion-card>
              </div>
            </ion-card-content>
          </ion-card>
        </ion-col>
      </ion-row>

      <!-- No Categories State -->
      <ion-row
        *ngIf="
          !isLoadingCategories &&
          (!categories || categories.categories.length === 0)
        "
      >
        <ion-col>
          <ion-card>
            <ion-card-content class="ion-text-center">
              <ion-icon
                name="library-outline"
                size="large"
                color="medium"
              ></ion-icon>
              <h3>No categories found</h3>
              <p>
                Start the onboarding process to begin automatic article
                categorization.
              </p>
              <ion-button (click)="startOnboarding()" color="primary">
                <ion-icon name="play-outline" slot="start"></ion-icon>
                Start Onboarding
              </ion-button>
            </ion-card-content>
          </ion-card>
        </ion-col>
      </ion-row>
    </div>
  </ion-grid>
</ion-content>
